/**
 * "IA local grátis" in Conectar agente (SDD ia-local-gratuita, task 7): the guided install. The
 * machine is probed and said plainly (RAM, free disk, GPU or none); every download happens behind
 * its own click that names the source and the size; the recommended model is always the one that
 * truly served the probes — qwen3:1.7b (the owner's call, 16/10) — and the catalog's honest note
 * about CPU minutes is shown, never hidden. Validation answers through the running Ollama; the
 * real-edit proof is the first chat turn, and the screen says so.
 */

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Cpu, Download, HardDrive, Loader2, Play, Sparkles } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { localized } from '../lib/prompts'
import { isDesktop } from '../lib/desktop'
import {
  localAiInstallOllama, localAiInstallOpencode, localAiProbe, localAiPull,
  localAiSetModel, localAiStart, localAiValidate, onLocalAiProgress, type LocalAiProgress,
} from '../lib/localAi'
import { Badge, Panel } from './primitives'

function problemText(t: (k: string, v?: Record<string, string | number>) => string, error: unknown): string {
  const code = String(error)
  for (const known of ['unsupported-system', 'download-not-verified', 'npm-not-installed', 'ollama-not-installed', 'ollama-did-not-answer', 'local-model-not-set', 'zstd-not-installed', 'unpack-failed']) {
    if (code.includes(known)) return t(`localAi.error.${known}`)
  }
  return t('localAi.error.unknown', { detail: code.slice(0, 120) })
}

export function LocalAiSetup() {
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()
  const [progress, setProgress] = useState<LocalAiProgress | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [validated, setValidated] = useState<string | null>(null)
  const [advanced, setAdvanced] = useState(false)
  const [customTag, setCustomTag] = useState('')

  const { data: probe, refetch } = useQuery({ queryKey: ['local-ai-probe'], queryFn: localAiProbe, enabled: isDesktop(), refetchInterval: 30_000 })
  const { data: catalog } = useQuery({ queryKey: ['local-models'], queryFn: api.localModels, staleTime: 10 * 60_000 })

  useEffect(() => {
    let cleanup: (() => void) | undefined
    void onLocalAiProgress((p) => setProgress(p.stage === 'done' ? null : p)).then((off) => {
      cleanup = off
    })
    return () => cleanup?.()
  }, [])

  const refresh = () => {
    setProgress(null)
    void queryClient.invalidateQueries({ queryKey: ['local-ai-probe'] })
    void refetch()
  }
  const useStep = (run: () => Promise<unknown>, after?: () => void) =>
    useMutationFor(run, refresh, setProblem, t, after)

  const installOllama = useStep(() => localAiInstallOllama())
  const installOpencode = useStep(() => localAiInstallOpencode())
  const start = useStep(() => localAiStart())
  const recommended = catalog?.models.find((m) => m.recommended)
  const pull = useStep(async () => {
    if (!recommended) throw new Error('local-model-not-set')
    await localAiStart()
    await localAiSetModel(recommended.tag)
    await localAiPull(recommended.tag)
  })
  // R4.3: a model of one's own, plainly marked untested by Vephus. Same path, the person's choice.
  const useCustom = useStep(async () => {
    const tag = customTag.trim()
    if (!tag) throw new Error('invalid-tag')
    await localAiStart()
    await localAiSetModel(tag)
    await localAiPull(tag)
  })
  const validate = useStep(async () => {
    const result = await localAiValidate()
    setValidated(result.answered ? t('localAi.validated', { seconds: result.answerSeconds }) : t('localAi.validateEmpty'))
  })

  if (!isDesktop() || !probe) return null
  if (!probe.supported) {
    return (
      <Panel title={<span className="row row--tight"><Sparkles size={15} aria-hidden /> {t('localAi.title')}</span>}>
        <span className="small muted">{t('localAi.unsupported')}</span>
      </Panel>
    )
  }

  const machine = [
    probe.ramGb != null ? `${t('localAi.ram')} ${probe.ramGb} GB` : null,
    probe.freeDiskGb != null ? `${t('localAi.disk')} ${probe.freeDiskGb} GB` : null,
    probe.gpu ?? t('localAi.noGpu'),
  ].filter(Boolean).join(' · ')
  const fits = recommended && probe.ramGb != null ? probe.ramGb >= recommended.ramGb : true
  const room = recommended && probe.freeDiskGb != null ? probe.freeDiskGb >= recommended.sizeGb + 3 : true
  const ready = probe.ollamaRunning && probe.modelPresent && (probe.opencodeInstalled || false)

  const rows: Array<{ key: string; done: boolean; busy: boolean; action: () => void; label: string; hint: string }> = [
    {
      key: 'ollama',
      done: probe.ollamaInstalled || probe.ollamaRunning,
      busy: installOllama.isPending,
      action: () => installOllama.mutate(),
      label: t('localAi.installOllama'),
      hint: t('localAi.installOllamaHint'),
    },
    {
      key: 'opencode',
      done: probe.opencodeInstalled,
      busy: installOpencode.isPending,
      action: () => installOpencode.mutate(),
      label: t('localAi.installOpencode'),
      hint: probe.npmInstalled ? t('localAi.installOpencodeHint') : t('localAi.error.npm-not-installed'),
    },
    {
      key: 'start',
      done: probe.ollamaRunning,
      busy: start.isPending,
      action: () => start.mutate(),
      label: t('localAi.start'),
      hint: t('localAi.startHint'),
    },
    {
      key: 'model',
      done: probe.modelPresent && Boolean(probe.model),
      busy: pull.isPending,
      action: () => pull.mutate(),
      label: recommended ? t('localAi.pull', { model: recommended.tag, size: recommended.sizeGb }) : t('localAi.pullNoCatalog'),
      hint: recommended ? localized(recommended.notes, locale) : '',
    },
  ]

  return (
    <Panel title={<span className="row row--tight"><Sparkles size={15} aria-hidden /> {t('localAi.title')}</span>}>
      <div className="stack">
        <span className="small muted">{t('localAi.what')}</span>
        <span className="tiny muted row row--tight"><Cpu size={12} aria-hidden /> {machine}</span>
        {recommended && (
          <span className="tiny muted row row--tight row--wrap">
            <Badge tone="accent">{t('localAi.recommended')}</Badge>
            <strong>{recommended.name}</strong> ({recommended.tag} · {recommended.license} · {recommended.sizeGb} GB)
            {!fits && <Badge tone="warn">{t('localAi.tightRam', { ram: recommended.ramGb })}</Badge>}
            {!room && <Badge tone="warn">{t('localAi.tightDisk')}</Badge>}
          </span>
        )}
        <ul className="machine-list">
          {rows.map((row) => (
            <li key={row.key} className="machine-row">
              <span className="machine-row__check" aria-hidden>{row.done ? <Check size={15} className="skills-notes__ok" /> : null}</span>
              <div className="stack" style={{ gap: 2, minWidth: 0, flex: 1 }}>
                <strong className="small">{row.label}</strong>
                {row.hint && <span className="tiny muted">{row.hint}</span>}
              </div>
              {!row.done && (
                <button type="button" className="btn btn--sm" onClick={row.action} disabled={row.busy}>
                  {row.busy ? <Loader2 size={13} className="spin" /> : <Download size={13} />} {t('localAi.doIt')}
                </button>
              )}
            </li>
          ))}
        </ul>
        {progress && (
          <div className="notice small row row--tight">
            <Loader2 size={13} className="spin" aria-hidden />
            {progress.detail}{progress.percent != null ? ` — ${Math.round(progress.percent)}%` : ''}
          </div>
        )}
        {ready && (
          <div className="row row--tight row--wrap">
            <button type="button" className="btn btn--sm" onClick={() => validate.mutate()} disabled={validate.isPending}>
              {validate.isPending ? <Loader2 size={13} className="spin" /> : <Play size={13} />} {t('localAi.validate')}
            </button>
            <span className="tiny muted">{validated ?? t('localAi.validateHint')}</span>
          </div>
        )}
        {ready && <span className="small skills-notes__ok row row--tight"><HardDrive size={13} aria-hidden /> {t('localAi.ready')}</span>}
        {probe.model && !probe.model.endsWith(`/${recommended?.tag ?? ''}`) && (
          <span className="tiny muted">{t('localAi.customInUse', { model: probe.model.replace('ollama/', '') })}</span>
        )}
        <button type="button" className="btn btn--ghost btn--sm" style={{ alignSelf: 'flex-start' }} onClick={() => setAdvanced(!advanced)}>
          {t('localAi.advanced')}
        </button>
        {advanced && (
          <div className="row row--tight row--wrap">
            <input
              className="input mono"
              style={{ minWidth: 220 }}
              value={customTag}
              onChange={(e) => setCustomTag(e.target.value)}
              placeholder="ex.: qwen2.5-coder:14b"
              aria-label={t('localAi.advanced')}
            />
            <button type="button" className="btn btn--sm" onClick={() => useCustom.mutate()} disabled={useCustom.isPending || !customTag.trim()}>
              {useCustom.isPending ? <Loader2 size={13} className="spin" /> : <Download size={13} />} {t('localAi.useCustom')}
            </button>
            <span className="tiny muted">{t('localAi.customHint')}</span>
          </div>
        )}
        {problem && <span className="tiny" style={{ color: 'var(--danger-text)' }}>{problem}</span>}
      </div>
    </Panel>
  )
}

/** One step's mutation: run, then re-probe; a refusal reads as a plain sentence. */
function useMutationFor(
  run: () => Promise<unknown>,
  refresh: () => void,
  setProblem: (p: string | null) => void,
  t: (k: string, v?: Record<string, string | number>) => string,
  after?: () => void,
) {
  return useMutation({
    mutationFn: run,
    onMutate: () => setProblem(null),
    onSuccess: () => {
      refresh()
      after?.()
    },
    onError: (error) => setProblem(problemText(t, error)),
  })
}
